import React from 'react';
import PropTypes from 'prop-types';
import { scrollTo } from '@firsttable/functions';
import Link from '@firsttable/web-components/atoms/Link';
import PriceRating from '@firsttable/web-components/molecules/PriceRating';
import Title from '@firsttable/web-components/atoms/Title';
import Rank from '@firsttable/web-components/molecules/Rank';
import Box from '@firsttable/web-components/atoms/Box';
import Flex from '@firsttable/web-components/atoms/Flex/Flex';
import LabelNew from '@firsttable/web-components/atoms/LabelNew';
import Breadcrumbs from '@firsttable/web-components/atoms/Breadcrumbs';
import styled from 'styled-components';
import { breakpointsObject as bp } from '@firsttable/web-theme';
import Stars from '@discover-components/restaurant/rating/stars';
import { TrackProvider } from '@/components/analytics/context/AnalyticsContext';
import RestaurantShare from '../Restaurant/Share';

const Grid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-template-rows: repeat(2, auto);
  @media (max-width: ${bp.m}) {
    grid-row-gap: 10px;
  }
`;

const restaurantTitleDefaultBreadcrumbs = {};
const RestaurantTitle = ({
  breadcrumbs = restaurantTitleDefaultBreadcrumbs,
  headerTitle = '',
  price = null,
  rank = null,
  rating = 0,
  reviews = 0,
  isNew = false,
  featuredText = null,
  NavLinkComponent,
  id = null,
  ...props
}) => (
  <>
    <TrackProvider component_name="breadcrumbs">
      <Breadcrumbs pb="s" itemScope itemType="http://schema.org/BreadcrumbList">
        {breadcrumbs.edges.map(({ node }, i) => (
          <li
            key={node.slug}
            itemScope
            itemProp="itemListElement"
            itemType="http://schema.org/ListItem">
            <TrackProvider link_name="breadcrumb">
              <Link to={node.slug} NavComponent={NavLinkComponent} itemProp="item" prefetch={false}>
                <Box as="span" itemProp="name">
                  {node.menuTitle}
                </Box>
              </Link>
            </TrackProvider>
            <meta itemProp="position" content={i + 1} />
          </li>
        ))}
      </Breadcrumbs>
    </TrackProvider>
    <Grid id="RestaurantTitle">
      <Box gridArea={['1 / 1 / 2 / 3', '1 / 1 / 2 / 2']} flex={1} display={[null, null, 'flex']}>
        <Flex mb={[0, 12]} alignItems="center">
          {rank !== 0 && (
            <Box m={['0 8px 0 0', '0 8px 0 0', '4px 8px 0 0']}>
              <Rank name="l-rank" rank={rank} />
            </Box>
          )}
          <Title as="h1" display="inline" mb="0px">
            {headerTitle}
          </Title>
          {isNew && <LabelNew ml={['s', null, 24]} />}
          {featuredText && (
            <LabelNew ml={!isNew ? ['s', null, 24] : [5, null, 10]}>{featuredText}</LabelNew>
          )}
        </Flex>
      </Box>

      <Box
        gridArea={['2 / 1 / 3 / 2', '2 / 1 / 3 / 3']}
        display="flex"
        flexDirection="row"
        alignItems="center"
        flexWrap="wrap"
        justifyContent="start">
        <Stars count={rating} size={19} gap={1.5} className="mr-4" />
        <TrackProvider link_name="reviews count">
          <Link
            underline
            onClick={() => scrollTo('RestaurantReviews')}
            color="grayscale.600"
            style={{ textWrap: 'nowrap' }}
            mr={16}>
            {reviews.toLocaleString()}
            {` `}
            {reviews === 1 ? 'review' : 'reviews'}
          </Link>
        </TrackProvider>
        {price && (
          <Box ml="l" display="inline">
            Price:&nbsp;&nbsp;
            <PriceRating display="inline-flex" rating={price} />
          </Box>
        )}
      </Box>
      <Box gridArea={['2 / 2 / 3 / 3', '1 / 2 / 2 / 3']}>
        <RestaurantShare {...props} className="-mt-2 sm:ml-8 sm:mr-0 sm:mt-3" />
      </Box>
    </Grid>
  </>
);

RestaurantTitle.propTypes = {
  headerTitle: PropTypes.string,
  breadcrumbs: PropTypes.object,
  price: PropTypes.number,
  rank: PropTypes.number,
  rating: PropTypes.number,
  reviews: PropTypes.number,
  isNew: PropTypes.bool,
  featuredText: PropTypes.string,
  NavLinkComponent: PropTypes.any.isRequired,
  id: PropTypes.number,
};
export default RestaurantTitle;
